import React, { useState } from 'react';
import { 
  BarChart, Bar, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell, ComposedChart,
  ReferenceLine
} from 'recharts';

const TTestAnalysisDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Group comparison data
  const groupComparisonData = [
    { metric: 'Total Score', expMean: 0.933, controlMean: 0.100, difference: 0.833, tValue: 2.820, pValue: 0.0048, significant: true, cohensD: 0.728, effectSize: 'Medium' },
    { metric: 'Band 1', expMean: 0.200, controlMean: 0.033, difference: 0.167, tValue: 1.348, pValue: 0.1778, significant: false, cohensD: 0.348, effectSize: 'Small' },
    { metric: 'Band 2', expMean: 0.367, controlMean: 0.100, difference: 0.267, tValue: 2.342, pValue: 0.0192, significant: true, cohensD: 0.605, effectSize: 'Medium' },
    { metric: 'Band 3', expMean: 0.200, controlMean: 0.033, difference: 0.167, tValue: 1.458, pValue: 0.1449, significant: false, cohensD: 0.376, effectSize: 'Small' },
    { metric: 'Band 4', expMean: 0.167, controlMean: 0.000, difference: 0.167, tValue: 2.013, pValue: 0.0441, significant: true, cohensD: 0.520, effectSize: 'Medium' },
    { metric: 'Band 5', expMean: 0.000, controlMean: -0.067, difference: 0.067, tValue: 1.017, pValue: 0.3091, significant: false, cohensD: 0.263, effectSize: 'Small' }
  ];

  // CERF level data
  const cerfLevelData = [
    { level: 'B1', expCount: 13, controlCount: 15, expMean: 1.154, controlMean: -0.1333, difference: 1.2873, tValue: 2.158, pValue: 0.0309, significant: true, cohensD: 0.851, effectSize: 'Large' },
    { level: 'B2', expCount: 17, controlCount: 15, expMean: 0.765, controlMean: 0.333, difference: 0.431, tValue: 1.518, pValue: 0.1290, significant: false, cohensD: 0.515, effectSize: 'Medium' }
  ];

  // Quartile data
  const quartileData = [
    { quartile: 'Q1 (Lowest)', expCount: 7, controlCount: 10, expMean: 1.857, controlMean: -0.300, difference: 2.157, tValue: 2.637, pValue: 0.0084, significant: true, cohensD: 1.435, effectSize: 'Large' },
    { quartile: 'Q2', expCount: 6, controlCount: 8, expMean: 1.500, controlMean: 0.125, difference: 1.375, tValue: 2.206, pValue: 0.0274, significant: true, cohensD: 1.374, effectSize: 'Large' },
    { quartile: 'Q3', expCount: 10, controlCount: 8, expMean: 0.500, controlMean: 0.500, difference: 0.000, tValue: 0.000, pValue: 1.0000, significant: false, cohensD: 0.000, effectSize: 'Negligible' },
    { quartile: 'Q4 (Highest)', expCount: 7, controlCount: 4, expMean: 0.143, controlMean: 0.250, difference: -0.107, tValue: -0.422, pValue: 0.6728, significant: false, cohensD: 0.282, effectSize: 'Small' }
  ];

  // Paired t-tests data - Experimental
  const pairedTestsExpData = [
    { metric: 'Total Score', preMean: 36.300, postMean: 37.233, difference: 0.933, tValue: 3.619, pValue: 0.0003, significant: true, cohensD: 0.661, effectSize: 'Medium' },
    { metric: 'Band 1', preMean: 7.967, postMean: 8.167, difference: 0.200, tValue: 2.301, pValue: 0.0214, significant: true, cohensD: 0.420, effectSize: 'Small' },
    { metric: 'Band 2', preMean: 7.500, postMean: 7.867, difference: 0.367, tValue: 3.673, pValue: 0.0002, significant: true, cohensD: 0.671, effectSize: 'Medium' },
    { metric: 'Band 3', preMean: 6.933, postMean: 7.133, difference: 0.200, tValue: 2.023, pValue: 0.0431, significant: true, cohensD: 0.369, effectSize: 'Small' },
    { metric: 'Band 4', preMean: 7.033, postMean: 7.200, difference: 0.167, tValue: 2.449, pValue: 0.0143, significant: true, cohensD: 0.447, effectSize: 'Small' },
    { metric: 'Band 5', preMean: 6.867, postMean: 6.867, difference: 0.000, tValue: 0.000, pValue: 1.0000, significant: false, cohensD: 0.000, effectSize: 'Negligible' }
  ];

  // Paired t-tests data - Control
  const pairedTestsControlData = [
    { metric: 'Total Score', preMean: 34.533, postMean: 34.633, difference: 0.100, tValue: 0.694, pValue: 0.4878, significant: false, cohensD: 0.127, effectSize: 'Negligible' },
    { metric: 'Band 1', preMean: 7.933, postMean: 7.967, difference: 0.033, tValue: 0.379, pValue: 0.7048, significant: false, cohensD: 0.069, effectSize: 'Negligible' },
    { metric: 'Band 2', preMean: 6.700, postMean: 6.800, difference: 0.100, tValue: 1.826, pValue: 0.0679, significant: false, cohensD: 0.333, effectSize: 'Small' },
    { metric: 'Band 3', preMean: 6.267, postMean: 6.300, difference: 0.033, tValue: 0.581, pValue: 0.5615, significant: false, cohensD: 0.106, effectSize: 'Negligible' },
    { metric: 'Band 4', preMean: 7.133, postMean: 7.133, difference: 0.000, tValue: 0.000, pValue: 1.0000, significant: false, cohensD: 0.000, effectSize: 'Negligible' },
    { metric: 'Band 5', preMean: 6.500, postMean: 6.433, difference: -0.067, tValue: -1.464, pValue: 0.1432, significant: false, cohensD: 0.267, effectSize: 'Small' }
  ];

  // Pre-Post comparison data for visualization
  const prePostComparisonData = [
    { group: 'Experimental', metric: 'Total Score', pre: 36.300, post: 37.233, change: 0.933, significant: true },
    { group: 'Experimental', metric: 'Band 1', pre: 7.967, post: 8.167, change: 0.200, significant: true },
    { group: 'Experimental', metric: 'Band 2', pre: 7.500, post: 7.867, change: 0.367, significant: true },
    { group: 'Experimental', metric: 'Band 3', pre: 6.933, post: 7.133, change: 0.200, significant: true },
    { group: 'Experimental', metric: 'Band 4', pre: 7.033, post: 7.200, change: 0.167, significant: true },
    { group: 'Experimental', metric: 'Band 5', pre: 6.867, post: 6.867, change: 0.000, significant: false },
    { group: 'Control', metric: 'Total Score', pre: 34.533, post: 34.633, change: 0.100, significant: false },
    { group: 'Control', metric: 'Band 1', pre: 7.933, post: 7.967, change: 0.033, significant: false },
    { group: 'Control', metric: 'Band 2', pre: 6.700, post: 6.800, change: 0.100, significant: false },
    { group: 'Control', metric: 'Band 3', pre: 6.267, post: 6.300, change: 0.033, significant: false },
    { group: 'Control', metric: 'Band 4', pre: 7.133, post: 7.133, change: 0.000, significant: false },
    { group: 'Control', metric: 'Band 5', pre: 6.500, post: 6.433, change: -0.067, significant: false }
  ];

  // Improvement data for direct comparison
  const improvementComparisonData = [
    { metric: 'Total Score', expImprovement: 0.933, controlImprovement: 0.100, significant: true },
    { metric: 'Band 1', expImprovement: 0.200, controlImprovement: 0.033, significant: false },
    { metric: 'Band 2', expImprovement: 0.367, controlImprovement: 0.100, significant: true },
    { metric: 'Band 3', expImprovement: 0.200, controlImprovement: 0.033, significant: false },
    { metric: 'Band 4', expImprovement: 0.167, controlImprovement: 0.000, significant: true },
    { metric: 'Band 5', expImprovement: 0.000, controlImprovement: -0.067, significant: false }
  ];

  // CERF level improvement visualization data
  const cerfImprovementData = [
    { level: 'B1', expImprovement: 1.154, controlImprovement: -0.1333, significant: true },
    { level: 'B2', expImprovement: 0.765, controlImprovement: 0.333, significant: false }
  ];

  // Quartile improvement visualization data
  const quartileImprovementData = [
    { quartile: 'Q1 (Lowest)', expImprovement: 1.857, controlImprovement: -0.300, significant: true },
    { quartile: 'Q2', expImprovement: 1.500, controlImprovement: 0.125, significant: true },
    { quartile: 'Q3', expImprovement: 0.500, controlImprovement: 0.500, significant: false },
    { quartile: 'Q4 (Highest)', expImprovement: 0.143, controlImprovement: 0.250, significant: false }
  ];

  const COLORS = {
    experimental: '#8884d8',
    control: '#82ca9d',
    significant: '#8884d8',
    nonSignificant: '#d1d5db',
    positive: '#4ade80',
    negative: '#f87171',
    neutral: '#d1d5db'
  };

  // Style objects
  const containerStyle = {
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    padding: '1.5rem'
  };

  const innerContainerStyle = {
    maxWidth: '80rem',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '2.5rem'
  };

  const titleStyle = {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    color: '#1F2937'
  };

  const subtitleStyle = {
    color: '#4B5563',
    marginTop: '0.5rem'
  };

  const navContainerStyle = {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'center'
  };

  const navBoxStyle = {
    backgroundColor: '#FFFFFF',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)'
  };

  const navStyle = {
    display: 'flex',
    overflowX: 'auto'
  };

  const navButtonBase = {
    padding: '0.75rem 1rem',
    fontWeight: 500,
    fontSize: '0.875rem',
    cursor: 'pointer',
    border: 'none',
    background: 'none'
  };

  const activeNavButtonStyle = {
    ...navButtonBase,
    color: '#2563eb',
    borderBottom: '2px solid #2563eb'
  };

  const inactiveNavButtonStyle = {
    ...navButtonBase,
    color: '#6B7280'
  };

  const gridTwoColumnsStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2rem'
  };

  const cardStyle = {
    backgroundColor: '#FFFFFF',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
    marginBottom: '2rem'
  };

  const cardTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1rem'
  };

  const footerStyle = {
    marginTop: '3rem',
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#6B7280'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
    marginTop: '1rem'
  };

  const thStyle = {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '1px solid #E5E7EB',
    color: '#374151',
    fontWeight: 600
  };

  const tdStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid #E5E7EB',
    color: '#4B5563'
  };

  const significantStyle = {
    backgroundColor: '#EFF6FF',
    fontWeight: 600
  };

  const chipStyle = {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: 'white', padding: '0.75rem', border: '1px solid #E5E7EB', borderRadius: '0.375rem' }}>
          <p style={{ margin: 0, fontWeight: 600 }}>{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} style={{ margin: '0.25rem 0', color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Function to get chip color based on p-value
  const getSignificanceChip = (pValue) => {
    const isSignificant = pValue <= 0.05;
    const style = {
      ...chipStyle,
      backgroundColor: isSignificant ? '#EFF6FF' : '#F3F4F6',
      color: isSignificant ? '#1E40AF' : '#6B7280'
    };
    
    return (
      <span style={style}>
        {isSignificant ? 'Significant' : 'Not Significant'}
      </span>
    );
  };

  // Function to get chip color based on effect size
  const getEffectSizeChip = (effectSize) => {
    let style = { ...chipStyle };
    
    switch(effectSize) {
      case 'Large':
        style.backgroundColor = '#DCFCE7';
        style.color = '#166534';
        break;
      case 'Medium':
        style.backgroundColor = '#EFF6FF';
        style.color = '#1E40AF';
        break;
      case 'Small':
        style.backgroundColor = '#FEF9C3';
        style.color = '#854D0E';
        break;
      default:
        style.backgroundColor = '#F3F4F6';
        style.color = '#6B7280';
    }
    
    return <span style={style}>{effectSize}</span>;
  };

  return (
    <div style={containerStyle}>
      <div style={innerContainerStyle}>
        <header style={headerStyle}>
          <h1 style={titleStyle}>T-Test Analysis Dashboard</h1>
          <p style={subtitleStyle}>Statistical analysis of receptive test improvements between experimental and control groups</p>
        </header>

        {/* Navigation Tabs */}
        <div style={navContainerStyle}>
          <div style={navBoxStyle}>
            <nav style={navStyle}>
              <button
                onClick={() => setActiveTab('overview')}
                style={activeTab === 'overview' ? activeNavButtonStyle : inactiveNavButtonStyle}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('cerfLevels')}
                style={activeTab === 'cerfLevels' ? activeNavButtonStyle : inactiveNavButtonStyle}
              >
                CERF Levels
              </button>
              <button
                onClick={() => setActiveTab('quartiles')}
                style={activeTab === 'quartiles' ? activeNavButtonStyle : inactiveNavButtonStyle}
              >
                Performance Quartiles
              </button>
              <button
                onClick={() => setActiveTab('pairedTests')}
                style={activeTab === 'pairedTests' ? activeNavButtonStyle : inactiveNavButtonStyle}
              >
                Within-Group Analysis
              </button>
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Main Improvement Comparison Chart */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Experimental vs. Control Group Improvements</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={improvementComparisonData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="metric" />
                  <YAxis label={{ value: 'Score Improvement', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="expImprovement" name="Experimental Group" fill={COLORS.experimental} />
                  <Bar dataKey="controlImprovement" name="Control Group" fill={COLORS.control} />
                  <ReferenceLine y={0} stroke="#666" />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p>Significant improvements in <strong>Total Score</strong>, <strong>Band 2</strong>, and <strong>Band 4</strong> for the experimental group.</p>
              </div>
            </div>

            <div style={gridTwoColumnsStyle}>
              {/* Summary Statistics */}
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Summary of Significant Findings</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={{ backgroundColor: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Total Score Improvement</div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>+0.833</div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>difference between groups</div>
                  </div>
                  <div style={{ backgroundColor: '#ECFDF5', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Effect Size</div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>0.728</div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>Cohen's d (Medium)</div>
                  </div>
                  <div style={{ backgroundColor: '#F3E8FF', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Significant Metrics</div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>3/6</div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>metrics showed significance</div>
                  </div>
                  <div style={{ backgroundColor: '#FEF9C3', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>p-value (Total)</div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>0.0048</div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>highly significant</div>
                  </div>
                </div>
              </div>

              {/* T-values visualization */}
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Statistical Significance (t-values)</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={groupComparisonData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="metric" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <ReferenceLine y={2.0} stroke="#ff0000" strokeDasharray="3 3" />
                    <Bar 
                      dataKey="tValue" 
                      name="t-value" 
                      fill={COLORS.experimental}
                      shape={(props) => {
                        const { x, y, width, height, value } = props;
                        const significant = Math.abs(value) > 2.0;
                        return (
                          <rect 
                            x={x} 
                            y={y} 
                            width={width} 
                            height={height} 
                            fill={significant ? COLORS.significant : COLORS.nonSignificant} 
                            radius={[0, 0, 0, 0]} 
                          />
                        );
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6B7280', textAlign: 'center' }}>
                  Red line indicates approximate significance threshold (t ≈ 2.0)
                </div>
              </div>
            </div>

            {/* Detailed Results Table */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Detailed Independent T-Test Results</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Metric</th>
                      <th style={thStyle}>Exp. Mean</th>
                      <th style={thStyle}>Control Mean</th>
                      <th style={thStyle}>Difference</th>
                      <th style={thStyle}>t-value</th>
                      <th style={thStyle}>p-value</th>
                      <th style={thStyle}>Significance</th>
                      <th style={thStyle}>Cohen's d</th>
                      <th style={thStyle}>Effect Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupComparisonData.map((item, index) => (
                      <tr key={index} style={item.significant ? significantStyle : {}}>
                        <td style={tdStyle}><strong>{item.metric}</strong></td>
                        <td style={tdStyle}>{item.expMean.toFixed(3)}</td>
                        <td style={tdStyle}>{item.controlMean.toFixed(3)}</td>
                        <td style={tdStyle}><strong>{item.difference.toFixed(3)}</strong></td>
                        <td style={tdStyle}>{item.tValue.toFixed(3)}</td>
                        <td style={tdStyle}>{item.pValue.toFixed(4)}</td>
                        <td style={tdStyle}>{getSignificanceChip(item.pValue)}</td>
                        <td style={tdStyle}>{item.cohensD.toFixed(3)}</td>
                        <td style={tdStyle}>{getEffectSizeChip(item.effectSize)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* CERF Levels Tab */}
        {activeTab === 'cerfLevels' && (
          <>
            {/* CERF Level Improvement Chart */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Improvement by CERF Level</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={cerfImprovementData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="level" />
                  <YAxis label={{ value: 'Score Improvement', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="expImprovement" name="Experimental Group" fill={COLORS.experimental} />
                  <Bar dataKey="controlImprovement" name="Control Group" fill={COLORS.control} />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p>B1 level learners in the experimental group showed significantly greater improvement (p=0.0309) with a large effect size (d=0.851).</p>
              </div>
            </div>

            <div style={gridTwoColumnsStyle}>
              {/* B1 Level Analysis */}
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>B1 Level Analysis</h2>
                <div style={{ backgroundColor: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Participant Count:</span>
                    <span style={{ fontSize: '0.875rem' }}>Experimental: 13, Control: 15</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Mean Improvement:</span>
                    <span style={{ fontSize: '0.875rem' }}>Experimental: 1.154, Control: -0.1333</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Difference:</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>1.2873</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>t-value:</span>
                    <span style={{ fontSize: '0.875rem' }}>2.158 (df=17)</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>p-value:</span>
                    <span style={{ fontSize: '0.875rem', color: '#1E40AF', fontWeight: 600 }}>0.0309 (Significant)</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Effect Size:</span>
                    <span style={{ fontSize: '0.875rem', color: '#166534', fontWeight: 600 }}>Large (d=0.851)</span>
                  </div>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  B1 learners showed the most dramatic difference between experimental and control groups, with experimental group showing significant improvement while control group showed no change.
                </p>
              </div>

              {/* B2 Level Analysis */}
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>B2 Level Analysis</h2>
                <div style={{ backgroundColor: '#F3F4F6', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Participant Count:</span>
                    <span style={{ fontSize: '0.875rem' }}>Experimental: 17, Control: 15</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Mean Improvement:</span>
                    <span style={{ fontSize: '0.875rem' }}>Experimental: 0.765, Control: 0.333</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Difference:</span>
                    <span style={{ fontSize: '0.875rem' }}>0.431</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>t-value:</span>
                    <span style={{ fontSize: '0.875rem' }}>1.518 (df=23)</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>p-value:</span>
                    <span style={{ fontSize: '0.875rem' }}>0.1290 (Not Significant)</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Effect Size:</span>
                    <span style={{ fontSize: '0.875rem', color: '#1E40AF' }}>Medium (d=0.515)</span>
                  </div>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  B2 learners in both groups showed improvement, though the experimental group improved more. The difference was not statistically significant, but the effect size was medium.
                </p>
              </div>
            </div>

            {/* CERF Level Comparison */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>CERF Level Comparison Summary</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div style={{ backgroundColor: '#F3E8FF', padding: '1.5rem', borderRadius: '0.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Key Findings for B1 Learners</h3>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                    <li>Experimental group showed significant improvement (p=0.0309)</li>
                    <li>Control group showed no improvement whatsoever</li>
                    <li>Large effect size (d=0.851) indicates substantial impact of treatment</li>
                    <li>Largest difference between experimental and control groups among all analyses</li>
                  </ul>
                </div>
                <div style={{ backgroundColor: '#ECFDF5', padding: '1.5rem', borderRadius: '0.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Key Findings for B2 Learners</h3>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                    <li>Both groups showed some improvement</li>
                    <li>Experimental group improved more, but difference wasn't statistically significant (p=0.1290)</li>
                    <li>Medium effect size (d=0.515) suggests treatment had moderate impact</li>
                    <li>Control group B2 learners improved more than control group B1 learners</li>
                  </ul>
                </div>
              </div>
              <div style={{ marginTop: '1.5rem', backgroundColor: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Implications</h3>
                <p style={{ fontSize: '0.875rem' }}>
                  The experimental treatment appears to be particularly effective for B1 level learners, where it shows the strongest effect. 
                  For B2 learners, the treatment shows positive but more modest results. This suggests the intervention might be most 
                  beneficial when targeted at intermediate (B1) rather than upper-intermediate (B2) learners.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Performance Quartiles Tab */}
        {activeTab === 'quartiles' && (
          <>
            {/* Quartile Improvement Chart */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Improvement by Initial Performance Quartile</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={quartileImprovementData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="quartile" />
                  <YAxis label={{ value: 'Score Improvement', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="expImprovement" name="Experimental Group" fill={COLORS.experimental} />
                  <Bar dataKey="controlImprovement" name="Control Group" fill={COLORS.control} />
                  <ReferenceLine y={0} stroke="#666" />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p>Lowest performing students (Q1 and Q2) in the experimental group showed the largest improvements, with statistically significant differences compared to control group.</p>
              </div>
            </div>

            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Detailed Quartile Analysis</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Quartile</th>
                      <th style={thStyle}>Exp. Count</th>
                      <th style={thStyle}>Control Count</th>
                      <th style={thStyle}>Exp. Mean</th>
                      <th style={thStyle}>Control Mean</th>
                      <th style={thStyle}>Difference</th>
                      <th style={thStyle}>t-value</th>
                      <th style={thStyle}>p-value</th>
                      <th style={thStyle}>Significance</th>
                      <th style={thStyle}>Cohen's d</th>
                      <th style={thStyle}>Effect Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quartileData.map((item, index) => (
                      <tr key={index} style={item.significant ? significantStyle : {}}>
                        <td style={tdStyle}><strong>{item.quartile}</strong></td>
                        <td style={tdStyle}>{item.expCount}</td>
                        <td style={tdStyle}>{item.controlCount}</td>
                        <td style={tdStyle}>{item.expMean.toFixed(3)}</td>
                        <td style={tdStyle}>{item.controlMean.toFixed(3)}</td>
                        <td style={tdStyle}><strong>{item.difference.toFixed(3)}</strong></td>
                        <td style={tdStyle}>{item.tValue.toFixed(3)}</td>
                        <td style={tdStyle}>{item.pValue.toFixed(4)}</td>
                        <td style={tdStyle}>{getSignificanceChip(item.pValue)}</td>
                        <td style={tdStyle}>{item.cohensD.toFixed(3)}</td>
                        <td style={tdStyle}>{getEffectSizeChip(item.effectSize)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={gridTwoColumnsStyle}>
              {/* Lower Quartiles Analysis */}
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Lower Quartiles (Q1-Q2)</h2>
                <div style={{ backgroundColor: '#DCFCE7', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem', color: '#166534' }}>Significant Improvements</h3>
                  <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Experimental group students in Q1 and Q2 showed dramatic improvements compared to control group:
                  </p>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                    <li><strong>Q1:</strong> +1.857 vs -0.300 (control actually declined)</li>
                    <li><strong>Q2:</strong> +1.500 vs +0.125</li>
                    <li>Both differences statistically significant (p&lt;0.05)</li>
                    <li>Both showed large effect sizes (d&gt;1.3)</li>
                  </ul>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  The experimental treatment appears to have the most dramatic positive impact on students who initially performed in the lowest quartiles.
                </p>
              </div>

              {/* Upper Quartiles Analysis */}
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Upper Quartiles (Q3-Q4)</h2>
                <div style={{ backgroundColor: '#F3F4F6', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem', color: '#6B7280' }}>No Significant Differences</h3>
                  <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    No significant differences between experimental and control groups in upper quartiles:
                  </p>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                    <li><strong>Q3:</strong> Both groups showed identical improvement (+0.500)</li>
                    <li><strong>Q4:</strong> Control group showed slightly more improvement (+0.250 vs +0.143)</li>
                    <li>Neither difference was statistically significant</li>
                    <li>Effect sizes were negligible to small</li>
                  </ul>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Higher-performing students showed modest improvements regardless of group assignment, suggesting the experimental treatment offered no additional advantage for students who were already performing well.
                </p>
              </div>
            </div>

            {/* Quartile Summary */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Quartile Analysis Summary</h2>
              <div style={{ backgroundColor: '#EFF6FF', padding: '1.5rem', borderRadius: '0.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Key Findings</h3>
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                  <li>The experimental treatment showed strong differential effectiveness based on initial performance level</li>
                  <li>Largest effects were observed for initially low-performing students (Q1 and Q2)</li>
                  <li>Q1 experimental group improved by 1.857 points while control group declined by 0.300 points</li>
                  <li>The effect size for Q1 was very large (d=1.435)</li>
                  <li>No significant benefits were observed for students in the upper performance quartiles (Q3 and Q4)</li>
                  <li>Control group Q4 students slightly outperformed experimental group Q4 students (not significant)</li>
                </ul>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '1rem 0', color: '#1E40AF' }}>Implications</h3>
                <p style={{ fontSize: '0.875rem' }}>
                  The experimental treatment appears to be particularly effective as an intervention for struggling students, 
                  helping to close the performance gap between lower and higher performers. This suggests the treatment might 
                  be best implemented as a targeted intervention for students performing below the median, rather than as a 
                  universal approach for all students.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Paired Tests Tab */}
        {activeTab === 'pairedTests' && (
          <>
            {/* Pre-Post Test Comparison */}


            <div style={gridTwoColumnsStyle}>
              {/* Experimental Group Paired Tests */}
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Experimental Group Paired T-Tests</h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Metric</th>
                        <th style={thStyle}>Pre-Mean</th>
                        <th style={thStyle}>Post-Mean</th>
                        <th style={thStyle}>Difference</th>
                        <th style={thStyle}>t-value</th>
                        <th style={thStyle}>p-value</th>
                        <th style={thStyle}>Significant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pairedTestsExpData.map((item, index) => (
                        <tr key={index} style={item.significant ? significantStyle : {}}>
                          <td style={tdStyle}><strong>{item.metric}</strong></td>
                          <td style={tdStyle}>{item.preMean.toFixed(2)}</td>
                          <td style={tdStyle}>{item.postMean.toFixed(2)}</td>
                          <td style={tdStyle}><strong>{item.difference.toFixed(3)}</strong></td>
                          <td style={tdStyle}>{item.tValue.toFixed(3)}</td>
                          <td style={tdStyle}>{item.pValue.toFixed(4)}</td>
                          <td style={tdStyle}>{getSignificanceChip(item.pValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#DCFCE7', borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                  <strong>Key Finding:</strong> Experimental group showed significant improvement in 5 out of 6 metrics (all except Band 5).
                </div>
              </div>

              {/* Control Group Paired Tests */}
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Control Group Paired T-Tests</h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Metric</th>
                        <th style={thStyle}>Pre-Mean</th>
                        <th style={thStyle}>Post-Mean</th>
                        <th style={thStyle}>Difference</th>
                        <th style={thStyle}>t-value</th>
                        <th style={thStyle}>p-value</th>
                        <th style={thStyle}>Significant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pairedTestsControlData.map((item, index) => (
                        <tr key={index} style={item.significant ? significantStyle : {}}>
                          <td style={tdStyle}><strong>{item.metric}</strong></td>
                          <td style={tdStyle}>{item.preMean.toFixed(2)}</td>
                          <td style={tdStyle}>{item.postMean.toFixed(2)}</td>
                          <td style={tdStyle}><strong>{item.difference.toFixed(3)}</strong></td>
                          <td style={tdStyle}>{item.tValue.toFixed(3)}</td>
                          <td style={tdStyle}>{item.pValue.toFixed(4)}</td>
                          <td style={tdStyle}>{getSignificanceChip(item.pValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#FEF2F2', borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                  <strong>Key Finding:</strong> Control group showed no significant improvement in any metric.
                </div>
              </div>
            </div>

            {/* Effect Size Comparison */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Effect Size Comparison</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[...pairedTestsExpData].map(item => ({
                    metric: item.metric,
                    experimental: item.cohensD,
                    control: pairedTestsControlData.find(c => c.metric === item.metric).cohensD
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="metric" />
                  <YAxis label={{ value: "Cohen's d", angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="experimental" name="Experimental Group" fill={COLORS.experimental} />
                  <Bar dataKey="control" name="Control Group" fill={COLORS.control} />
                  <ReferenceLine y={0.5} stroke="#ff0000" strokeDasharray="3 3" label={{ value: 'Medium Effect', position: 'insideBottomRight' }} />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p>The experimental group showed medium effect sizes (d>0.5) for Total Score and Band 2, with small to medium effects for other bands (except Band 5).</p>
              </div>
            </div>

            {/* Within-Group Summary */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Within-Group Analysis Summary</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div style={{ backgroundColor: '#EFF6FF', padding: '1.5rem', borderRadius: '0.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#1E40AF' }}>Experimental Group</h3>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                    <li>Significant improvement in Total Score (p=0.0003)</li>
                    <li>Significant improvements in 4 out of 5 bands</li>
                    <li>Strongest improvement in Band 2 (p=0.0002, d=0.671)</li>
                    <li>No significant change in Band 5 (most difficult items)</li>
                    <li>Medium effect size for Total Score (d=0.661)</li>
                  </ul>
                </div>
                <div style={{ backgroundColor: '#F3F4F6', padding: '1.5rem', borderRadius: '0.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#6B7280' }}>Control Group</h3>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                    <li>No significant improvement in Total Score (p=0.4878)</li>
                    <li>No significant improvements in any band</li>
                    <li>Closest to significance was Band 2 (p=0.0679)</li>
                    <li>Slight non-significant decline in Band 5 (-0.067)</li>
                    <li>All effect sizes negligible to small</li>
                  </ul>
                </div>
              </div>
              <div style={{ marginTop: '1.5rem', backgroundColor: '#DCFCE7', padding: '1rem', borderRadius: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Key Implications</h3>
                <p style={{ fontSize: '0.875rem' }}>
                  The within-group analysis strongly supports the effectiveness of the experimental treatment. While the experimental 
                  group showed significant improvements across nearly all metrics, the control group showed no significant improvements 
                  in any area. The treatment appears to have its strongest effects on mid-difficulty items (Band 2), with moderate effects 
                  on easier items (Band 1) and more challenging items (Bands 3-4). The treatment did not significantly impact the most 
                  difficult items (Band 5).
                </p>
              </div>
            </div>
          </>
        )}

        <footer style={footerStyle}>
          <p>T-Test Analysis Dashboard | Statistical comparison of experimental vs. control group performance</p>
        </footer>
      </div>
    </div>
  );
};

export default TTestAnalysisDashboard;